import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import Dialog from '@/components/mui/Dialog';
import elements from '@/components/elements';
import SelectBase from '@/components/mui/Form/Select/SelectBase';
import TextFieldRhf from '@/components/mui/Form/TextField/TextFieldRhf';
import { createCompanyAction } from '@/domain/companies';
import { organizationCountrySelector } from '@/domain/organization';
import { countriesWithExtraInfo } from '@/lib/countries';
import { renderCountryOptions } from '@/components/mui/Form/Select/selectRenderers';

import UploadImg from '@/pages/companies/components/CreateCompany/components/UploadImg';
import {
  ACCEPTED_FILE_TYPES,
  createCompanySchema,
  type CreateCompanyValues,
} from '@/pages/companies/components/CreateCompany/schema';

interface CreateCompanyProps {
  open: boolean;
  onClose: () => void;
}

// Its own component so the form mounts and unmounts with the dialog.
// `defaultValues` and `isValid` are read once, at mount, and neither reset()
// nor async defaults recompute them — and the parent stays mounted while the
// dialog is shut, so a form living up there would keep the last attempt.
const CreateCompanyForm: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const organizationCountry = useSelector(organizationCountrySelector);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid, isSubmitting },
  } = useForm<CreateCompanyValues>({
    resolver: zodResolver(createCompanySchema),
    // The legacy form set touchOnChange, so a field started showing its message
    // from the first keystroke rather than from the first blur.
    mode: 'onChange',
    defaultValues: {
      name: '',
      // The organization's country can arrive null — an Immutable Record falls
      // back to its default only for a missing key, never for an explicit null.
      country: organizationCountry ?? '',
      registration_number: '',
      vat_number: '',
      picture: null,
    },
  });

  const isExtraDataRequired = countriesWithExtraInfo.includes(watch('country'));

  // Awaited so `isSubmitting` gates the button for the request's whole life.
  // The rejection is swallowed because the saga has already toasted it, and
  // handleSubmit re-throws whatever onSubmit rejects with.
  const onSubmit = (values: CreateCompanyValues) => {
    const formData = new global.FormData();
    // Empty fields are left out, which is what the legacy form sent too: its
    // store dropped a value from the payload as soon as it went back to ''.
    Object.entries(values).forEach(([key, value]) => {
      if (value) formData.append(key, value);
    });

    return new Promise<void>((resolve, reject) => {
      dispatch(createCompanyAction({ formData, resolve, reject }));
    }).catch(() => {});
  };

  return (
    // noValidate: validation is the schema's job. `required` stays for the
    // asterisk, but MUI also puts it on the input, and the native bubble fires
    // before the resolver and hides its message.
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <DialogContent>
        <Grid container alignItems="center" justifyContent="center" width="100%" mb={2.5}>
          <Controller
            name="picture"
            control={control}
            render={({ field: { value, onChange }, fieldState }) => (
              <UploadImg
                name="picture"
                accept={ACCEPTED_FILE_TYPES.join(',')}
                file={value}
                onChange={onChange}
                errorCode={fieldState.error?.message}
              />
            )}
          />
        </Grid>

        <Typography variant="body2" mb={2.5} fontSize={12}>
          {formatMessage({ id: 'form.company.create.inputs.label', defaultMessage: 'Company information' })}
        </Typography>

        <Box display="flex" flexDirection="column" gap={2.5} width="100%">
          <TextFieldRhf
            name="name"
            control={control}
            label={formatMessage({ id: 'form.company.create.name.label', defaultMessage: 'Company name' })}
            placeholder={formatMessage({ id: 'form.company.create.name.placeholder', defaultMessage: 'Company name' })}
            slotProps={{ htmlInput: { maxLength: 64 } }}
            size="small"
            fullWidth
            required
          />

          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <SelectBase
                {...field}
                id="country"
                label={formatMessage({ id: 'forms.common.main.country.label', defaultMessage: 'Country' })}
                renderOptions={renderCountryOptions}
                FormControlProps={{ fullWidth: true, size: 'small', required: true }}
              />
            )}
          />

          <TextFieldRhf
            name="registration_number"
            control={control}
            label={formatMessage({
              id: 'forms.common.registration.label',
              defaultMessage: 'Company Registration Number',
            })}
            slotProps={{ htmlInput: { maxLength: 64 } }}
            size="small"
            fullWidth
            required={isExtraDataRequired}
            data-element={elements.configuration.general.financial.registrationNumber}
          />

          <TextFieldRhf
            name="vat_number"
            control={control}
            label={formatMessage({ id: 'forms.common.vat.label', defaultMessage: 'VAT number' })}
            slotProps={{ htmlInput: { maxLength: 64 } }}
            size="small"
            fullWidth
            required={isExtraDataRequired}
            data-element={elements.configuration.general.financial.vat}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button variant="text" onClick={onClose}>
          {formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' })}
        </Button>
        <Button variant="contained" type="submit" loading={isSubmitting} disabled={isSubmitting || !isValid}>
          {formatMessage({ id: 'button.create', defaultMessage: 'Create' })}
        </Button>
      </DialogActions>
    </form>
  );
};

const CreateCompany: React.FC<CreateCompanyProps> = ({ open, onClose }) => {
  const { formatMessage } = useIntl();

  return (
    <Dialog
      withCloseBtn
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={formatMessage({ id: 'form.company.create.title', defaultMessage: 'Company Registration' })}
      withActions={false}
      withContent={false}
    >
      <CreateCompanyForm onClose={onClose} />
    </Dialog>
  );
};

export default CreateCompany;
